import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Application from "expo-application";

import analytics from "@/analytics";
import { useAuth } from "@/components/AuthContext";
import { isMacApp } from "@/components/macos/macApp";
import { currentUpdateLabel } from "@/components/updates";
import bundled from "./paywall-test.json";
import {
  assignPaywall,
  normalizeConfig,
  previewChoice,
} from "./paywallTestLogic";

// Paywall A/B test: which paywall the signed-in account sees. Rules and the
// on/off switch: docs/paywall-test.md.
//
// - The config is components/experiments/paywall-test.json. The web app (and
//   the Mac app, which hosts it) ships with it; the build also publishes it at
//   CONFIG_URL, and the iOS and Android apps fetch that copy at launch, so
//   switching the test on or off reaches every app when `main` deploys.
// - Until the published copy has loaded, the apps use the last copy they
//   fetched, then the one in the build. The paywall waits up to
//   CONFIG_WAIT_MS for it, alongside the prices it already waits for.

export const CONFIG_URL = "https://broadcaster.eventlive.pro/paywall-test.json";
const CACHE_KEY = "eventlive.paywallTest.config.v1";
const CONFIG_WAIT_MS = 2500;
const FETCH_TIMEOUT_MS = 4000;

const isNative = Platform.OS === "ios" || Platform.OS === "android";

let state = { config: normalizeConfig(bundled), settled: !isNative };
const listeners = new Set();
const emit = (next) => {
  state = { ...state, ...next };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getState = () => state;

let loading = null;

// Deploy previews and localhost: ?paywall_test=picker|tiers (see
// previewChoice). Kept for the browser tab, so it survives signing in and
// moving around; read when the app loads and again on each paywall visit.
const PREVIEW_KEY = "eventlive.paywallTest.preview";
function previewVariant() {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  try {
    const storage = window.sessionStorage;
    const { variant, save } = previewChoice(
      window.location?.hostname,
      window.location?.search,
      storage?.getItem(PREVIEW_KEY)
    );
    if (save === "") storage?.removeItem(PREVIEW_KEY);
    else if (save) storage?.setItem(PREVIEW_KEY, save);
    return variant;
  } catch {
    return null;
  }
}
previewVariant();

// The config in use right now (support and tests).
export const currentPaywallTestConfig = () => state.config;

// Starts loading the published config. Called when the app starts
// (BuyContext) so it's there by the time a new account reaches the paywall.
export function prefetchPaywallTest() {
  if (!isNative || loading) return loading;
  loading = (async () => {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY).catch(() => null);
      const fromCache = cached ? normalizeConfig(safeParse(cached)) : null;
      if (fromCache) emit({ config: fromCache });
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = setTimeout(() => controller?.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(CONFIG_URL, {
          headers: { Accept: "application/json" },
          signal: controller?.signal,
        });
        if (res.ok) {
          const json = await res.json();
          const config = normalizeConfig(json);
          if (config) {
            emit({ config });
            AsyncStorage.setItem(CACHE_KEY, JSON.stringify(json)).catch(
              () => {}
            );
          }
        }
      } finally {
        clearTimeout(timer);
      }
    } catch {
      // Offline: keep the cached or bundled config.
    } finally {
      emit({ settled: true });
    }
  })();
  return loading;
}

function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const clientPlatform = () =>
  Platform.OS === "ios" || Platform.OS === "android" ? Platform.OS : "web";

// Where the paywall was seen, for reporting: the Mac app runs the web build.
const clientName = () =>
  Platform.OS === "web" ? (isMacApp() ? "mac" : "web") : Platform.OS;

// { ready, enrolled, variant, testId, bucket, reason }. Not ready while the
// published config (apps) or, with the test on, the profile is still loading;
// ready anyway after CONFIG_WAIT_MS, then without the test if anything is
// missing. Once ready, the answer is kept for this paywall visit so the
// layout never switches under the user.
export function usePaywallTest() {
  const {
    profileLoaded,
    username,
    broadcaster,
    account_type,
    legacyPricing,
    registered_datetime,
    email,
  } = useAuth();
  const snap = useSyncExternalStore(subscribe, getState, getState);
  const [waited, setWaited] = useState(false);
  const [preview] = useState(previewVariant);
  const kept = useRef(null);

  useEffect(() => {
    prefetchPaywallTest();
    const timer = setTimeout(() => setWaited(true), CONFIG_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  const account = username || broadcaster;
  const live = useMemo(() => {
    const on = snap.config?.status === "on";
    const ready = waited || (snap.settled && (!on || !!profileLoaded));
    const result = assignPaywall(
      snap.config,
      {
        username: account,
        account_type,
        legacyPricing,
        registered_datetime,
        email,
      },
      {
        platform: clientPlatform(),
        appVersion: isNative ? Application.nativeApplicationVersion : null,
      }
    );
    return { ready, testId: snap.config?.id || null, ...result };
  }, [
    profileLoaded,
    snap,
    waited,
    account,
    account_type,
    legacyPricing,
    registered_datetime,
    email,
  ]);

  if (preview) {
    return {
      ready: true,
      enrolled: true,
      variant: preview,
      testId: "preview",
      bucket: null,
      reason: "preview",
    };
  }
  if (live.ready && !kept.current) kept.current = live;
  return kept.current || live;
}

// Logs that this account saw its paywall in the test: an event for the
// funnel, and user properties so every later event (including purchases the
// server logs) can be split by paywall.
const identified = new Set();
export function trackPaywallTestView(test, username, details = {}) {
  // Preview visits aren't part of the test.
  if (!test?.enrolled || !test.testId || test.testId === "preview") return;
  const props = {
    test_id: test.testId,
    variant: test.variant,
    bucket: test.bucket,
    client: clientName(),
    app_version: isNative ? Application.nativeApplicationVersion : null,
    update: currentUpdateLabel(),
    ...details,
  };
  analytics.track("Paywall Test Viewed", props);
  const key = `${test.testId}:${username}`;
  if (username && !identified.has(key)) {
    identified.add(key);
    analytics.identify(username, {
      paywall_test: test.testId,
      paywall_variant: test.variant,
    });
  }
}

// Test-only: reset the module state between tests.
export function __resetPaywallTestForTests(config) {
  state = { config: normalizeConfig(config ?? bundled), settled: !isNative };
  loading = null;
  identified.clear();
}
